import { LOOKS, Look } from "./look";

/**
 * The rare spawn's "golden aurora night" (M36 §5): while a rare lives the map sinks into a starlit night lit gold —
 * a low warm-gold key, a deep violet-blue zenith, the horizon tinted the rare's own aurora colour, deep shadows, a
 * heavier vignette and bloom that catches every glint — and when it falls the dusk lifts through the sun-break
 * overshoot (look.ts SUN_BREAK) back to the afternoon; when it escapes, the night drains away cold and quick.
 *
 * Like BOSS_DUSK it is a blend target for `SceneRig.blendLooks`, never `setLook` (no PMREM rebuild, no hitch). This
 * file is the looks and the timeline only — pure numbers, no three.js — so it is unit-tested.
 */

/** The night for a rare whose aurora horizon is `sky` (0xRRGGBB, rareApi.ts RareDef.sky). Cached per colour. */
export function rareNight(sky: number): Look {
  const hit = cache.get(sky);
  if (hit) return hit;
  const L: Look = {
    ...LOOKS.painterly,
    name: `rareNight-${sky.toString(16)}`,
    exposure: 0.98,
    sun: { color: 0xffbe5a, intensity: 1.9, elevation: 50 },
    fill: { color: 0x7a66ff, intensity: 0.95 },
    hemi: { sky: 0x7f78d0, ground: 0x2e2040, intensity: 0.62 },
    sky, fog: mixHex(sky, 0x241e48, 0.5), skyTop: 0x141850, envSun: 0xffd890,
    bloom: { strength: 0.9, radius: 0.62, threshold: 0.9, knee: 0.2 },
    grade: { contrast: 1.18, saturation: 1.1, vignette: 0.5, shadows: 0x8c84ec, highlights: 0xffd890, lift: 0.0 },
  };
  cache.set(sky, L);
  return L;
}
const cache = new Map<number, Look>();

function mixHex(a: number, b: number, t: number): number {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - t) + ((b >> s) & 255) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/**
 * The timeline of the rare's look: `spawned` sinks into the night over InSeconds (smoothstep) and holds; `killed`
 * lifts it over OutSeconds while the sun-break overshoot rises and settles (as BossDusk's); `escaped` drains it over
 * EscapeSeconds with no overshoot. `weights.night`/`weights.sun` go to `SceneRig.blendLooks`; `weights.sparkle` (0–1)
 * drives the world sparkles and follows the night, a beat faster.
 */
export class RareGlow {
  static readonly InSeconds = 2.4;
  static readonly OutSeconds = 2.0;
  static readonly EscapeSeconds = 1.2;
  static readonly BreakRise = 0.6;
  static readonly BreakSeconds = 3.4;

  private alive = false;
  private p = 0;
  private tb = -1;
  private out = RareGlow.OutSeconds;
  /** The night's look while one is up (null: none since the last reset). */
  look: Look | null = null;
  readonly weights = { night: 0, sun: 0, sparkle: 0, active: false };

  spawned(look: Look): void { this.look = look; this.alive = true; this.tb = -1; }
  killed(): void { if (!this.alive) return; this.alive = false; this.out = RareGlow.OutSeconds; this.tb = 0; }
  escaped(): void { if (!this.alive) return; this.alive = false; this.out = RareGlow.EscapeSeconds; this.tb = -1; }
  get living(): boolean { return this.alive; }

  reset(): void { this.alive = false; this.p = 0; this.tb = -1; const w = this.weights; w.night = w.sun = w.sparkle = 0; w.active = false; }

  update(dt: number): typeof this.weights {
    const step = this.alive ? dt / RareGlow.InSeconds : -dt / this.out;
    this.p = Math.max(0, Math.min(1, this.p + step));
    let sun = 0;
    if (this.tb >= 0) {
      this.tb += dt;
      const t = this.tb;
      if (t >= RareGlow.BreakSeconds || this.alive) this.tb = -1;
      else if (t < RareGlow.BreakRise) { const u = t / RareGlow.BreakRise; sun = 1 - (1 - u) * (1 - u); }
      else { const u = (t - RareGlow.BreakRise) / (RareGlow.BreakSeconds - RareGlow.BreakRise); sun = 1 - u * u * (3 - 2 * u); }
    }
    const w = this.weights;
    w.night = this.p * this.p * (3 - 2 * this.p);
    w.sun = sun;
    w.sparkle = Math.min(1, this.p * 1.4);
    w.active = w.night > 0 || w.sun > 0;
    return w;
  }
}
